import React from 'react';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Box, IconButton, Checkbox, FormControlLabel, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import amplifyConfig from './config/amplify';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import OAuthCallback from './components/OAuthCallback';
import PrivacyPolicyDialog from './components/PrivacyPolicyDialog';
import '@aws-amplify/ui-react/styles.css';
import './styles/authenticator.css';

Amplify.configure(amplifyConfig);

const theme = createTheme({
  palette: {
    primary: { 
      main: '#9C27B0', // Purple
      light: '#BA68C8',
      dark: '#7B1FA2',
    },
    secondary: { 
      main: '#FF9800', // Orange
      light: '#FFB74D',
      dark: '#F57C00',
    },
    info: {
      main: '#00BCD4', // Teal/Cyan
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
        contained: {
          boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(156, 39, 176, 0.4)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
  },
});

function App() {
  const [privacyOpen, setPrivacyOpen] = React.useState(false);
  const [agreedToTerms, setAgreedToTerms] = React.useState(false);

  const formFields = {
    signUp: {
      email: { order: 1 },
      password: { order: 2 },
      confirm_password: { order: 3 }
    }
  };

  const components = {
    SignUp: {
      FormFields() {
        return (
          <>
            <Authenticator.SignUp.FormFields />
            <div className="privacy-checkbox-container">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    required
                  />
                }
                label={
                  <span style={{ fontSize: '0.875rem' }}>
                    I agree to the
                    <span className="privacy-link" onClick={() => setPrivacyOpen(true)}>
                      Terms of Service and Privacy Policy
                    </span>
                    , including AI content disclaimer
                  </span>
                }
              />
            </div>
          </>
        );
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PrivacyPolicyDialog open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
      <Router>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/callback" element={
            <Box>
              <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 1000 }}>
                <IconButton component={Link} to="/" sx={{ color: 'white' }}>
                  <ArrowBack />
                </IconButton>
              </Box>
              <Authenticator hideSignUp>
                {({ signOut, user }) => <OAuthCallback />}
              </Authenticator>
            </Box>
          } />
          <Route path="/app/*" element={
            <Box>
              <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 1000 }}>
                <IconButton component={Link} to="/" sx={{ color: 'white' }}>
                  <ArrowBack />
                </IconButton>
              </Box>
              <Authenticator hideSignUp>
                {({ signOut, user }) => (
                  <Routes>
                    <Route path="/" element={<Dashboard signOut={signOut} user={user} />} />
                    <Route path="/settings" element={<Settings signOut={signOut} user={user} />} />
                  </Routes>
                )}
              </Authenticator>
            </Box>
          } />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
